import type { ExerciseTypeConfig } from '../registry/types';
import type { 
  MatchingExerciseDefinition, 
  MatchingExerciseSubmission,
  MatchingEvaluationDetail 
} from '../types';
import { matchingSubmissionSchema } from '../types/schemas';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: MatchingExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * Matching exercise type configuration
 */
export const matchingConfig: ExerciseTypeConfig<
  'Matching',
  MatchingExerciseDefinition,
  MatchingExerciseSubmission,
  MatchingEvaluationDetail
> = {
  type: 'Matching',
  isAutoGradable: true,
  submissionSchema: matchingSubmissionSchema,

  defaultMaxScore: (definition) => {
    const totalPairs = definition.pairs?.length ?? 0;
    return getNormalizedMaxScore(definition, Math.max(1, totalPairs));
  },

  evaluate: (definition, submission) => {
    const pairs = definition.pairs ?? [];
    const userMatches = submission.matches ?? [];

    // Build a lookup from promptId -> user's selectedOptionId
    const userMatchMap = new Map<string, string>();
    for (const match of userMatches) {
      userMatchMap.set(match.promptId, match.selectedOptionId);
    }

    const pairEvaluations = pairs.map((pair) => {
      const correctOptionId = pair.optionId;
      const selectedOptionId = userMatchMap.get(pair.promptId) ?? null;
      const isCorrect = selectedOptionId === correctOptionId;

      return {
        promptId: pair.promptId,
        correctOptionId,
        selectedOptionId,
        isCorrect,
      };
    });

    const totalCorrect = pairEvaluations.filter((p) => p.isCorrect).length;
    const totalPairs = pairs.length;
    const maxScore = getNormalizedMaxScore(definition, Math.max(1, totalPairs));

    return {
      score: totalPairs === 0 ? 0 : Math.round((totalCorrect / totalPairs) * maxScore),
      maxScore,
      isFullyCorrect: totalPairs > 0 && totalCorrect === totalPairs,
      details: {
        kind: 'Matching',
        pairEvaluations,
      },
    };
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    
    // Handle both `pairs` array (from server) and `prompts` array (from admin editor)
    if (Array.isArray(details.pairs)) {
      return {
        pairs: details.pairs.map((pair: any, index: number) => ({
          pairId: pair?.pairId,
          promptId: pair?.promptId ?? pair?.pairId,
          promptLabel: pair?.promptLabel ?? '',
          optionId: pair?.optionId,
          optionLabel: pair?.optionLabel ?? '',
          order: pair?.order ?? index,
        })),
      };
    }

    // Fallback: if prompts are provided (from admin editor), extract pairs
    if (Array.isArray(details.prompts)) {
      return {
        pairs: details.prompts.map((prompt: any, index: number) => {
          const correctOption = (prompt.options ?? []).find((option: any) => option.isCorrect);
          return {
            pairId: prompt.promptId,
            promptId: prompt.promptId,
            promptLabel: prompt.promptText ?? '',
            optionId: correctOption?.optionId ?? '',
            optionLabel: correctOption?.optionText ?? '',
            order: prompt.order ?? index,
          };
        }),
      };
    }

    return { pairs: [] };
  },

  createDefault: (order) => ({
    type: 'Matching' as const,
    pairs: [
      { pairId: undefined, promptId: undefined, promptLabel: '', optionId: undefined, optionLabel: '', order: 0 },
      { pairId: undefined, promptId: undefined, promptLabel: '', optionId: undefined, optionLabel: '', order: 1 },
    ],
  }),
};
